import { floorToEuro, formatEUR, type SavingsGoalResponse } from '@budget/shared';
import { type RefObject, useState } from 'react';
import { ApiError } from '../api/client';
import { useSavingsTransfer, useWithdrawFromSavings } from '../api/hooks';
import { AmountField } from './AmountField';
import { BottomSheet } from './BottomSheet';
import { accountGlyph, goalGlyph } from './glyphs';

/**
 * Sens du mouvement, décidé par l'état du compte courant :
 * en avance on met de côté, en retard — ou à découvert — on renfloue.
 */
export type MoveDirection = 'deposit' | 'withdraw';

interface Props {
  goal: SavingsGoalResponse;
  direction: MoveDirection;
  /** Compte courant concerné, source ou destination selon le sens. */
  accountId: string;
  /** Solde du compte courant, montré dans le schéma du mouvement. */
  accountBalanceCents: number;
  /** Son pictogramme, pour que le schéma parle le même langage que l'app. */
  accountIcon: string;
  /** Montant proposé d'emblée : l'avance à placer, ou le découvert à combler. */
  suggestedCents: number;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

function FlowNode({
  glyph,
  title,
  amountCents,
}: {
  glyph: string;
  title: string;
  amountCents: number;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center">
      <span
        aria-hidden="true"
        className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-plane text-xl"
      >
        {glyph}
      </span>
      <p className="w-full truncate text-xs text-ink-secondary" title={title}>
        {title}
      </p>
      <p className={`tabular text-sm font-semibold ${amountCents < 0 ? 'text-critical' : ''}`}>
        {formatEUR(amountCents)}
      </p>
    </div>
  );
}

/** Flèche du mouvement : les montants sous les nœuds portent le sens, elle l'illustre. */
function FlowArrow() {
  return (
    <svg viewBox="0 0 40 16" className="mt-3 h-4 w-10 shrink-0" aria-hidden="true">
      <line
        x1="2"
        y1="8"
        x2="30"
        y2="8"
        stroke="var(--series)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M30 3 L37 8 L30 13"
        fill="none"
        stroke="var(--series)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SavingsMoveModal({
  goal,
  direction,
  accountId,
  accountBalanceCents,
  accountIcon,
  suggestedCents,
  anchorRef,
  onClose,
}: Props) {
  const deposit = useSavingsTransfer();
  const withdraw = useWithdrawFromSavings();

  const isDeposit = direction === 'deposit';
  // Un retrait ne peut pas dépasser ce que le pôle contient ; une mise de côté
  // n'a pas de plafond dur, seulement un avertissement.
  const ceilingCents = isDeposit ? Number.POSITIVE_INFINITY : goal.savedCents;

  const [amountCents, setAmountCents] = useState(
    Math.min(floorToEuro(Math.max(suggestedCents, 0)), ceilingCents),
  );
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const pending = deposit.isPending || withdraw.isPending;
  const overCeiling = amountCents > ceilingCents;
  const overSuggestion = isDeposit && amountCents > suggestedCents;

  async function submit() {
    if (amountCents <= 0 || overCeiling) return;
    setError(null);
    try {
      if (isDeposit) {
        await deposit.mutateAsync({ goalId: goal.id, amountCents });
      } else {
        await withdraw.mutateAsync({ goalId: goal.id, toAccountId: accountId, amountCents });
      }
      setDone(amountCents);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Virement impossible.');
    }
  }

  const label = isDeposit ? `Mettre de côté sur ${goal.name}` : 'Alimenter le compte courant';

  // Le schéma reprend les pictogrammes choisis à la création : c'est ce qui le
  // rend reconnaissable d'un coup d'œil plutôt que générique.
  const savingsNode = (
    <FlowNode glyph={goalGlyph(goal)} title={goal.name} amountCents={goal.savedCents} />
  );
  const currentNode = (
    <FlowNode
      glyph={accountGlyph({ icon: accountIcon, type: 'checking' })}
      title="Compte courant"
      amountCents={accountBalanceCents}
    />
  );

  return (
    <BottomSheet anchorRef={anchorRef} label={label} onClose={onClose}>
      {/* Le schéma remplace la phrase : deux comptes, une flèche, les soldes
          dessous. Le sens du mouvement se lit d'un coup d'œil. */}
      <figure
        aria-label={
          isDeposit
            ? `Du compte courant vers ${goal.name}`
            : `De ${goal.name} vers le compte courant`
        }
        className="mt-4 flex items-start justify-center gap-2"
      >
        {isDeposit ? currentNode : savingsNode}
        <FlowArrow />
        {isDeposit ? savingsNode : currentNode}
      </figure>

      <dl className="mt-4 grid grid-cols-[1fr_auto] gap-y-1 text-sm">
        <dt className="text-ink-secondary">
          {isDeposit ? 'Avance disponible' : 'Écart à équilibrer'}
        </dt>
        <dd className="tabular">{formatEUR(Math.max(suggestedCents, 0))}</dd>
      </dl>

      {done !== null ? (
        <>
          <p role="status" className="mt-4 text-sm text-success-text">
            {formatEUR(done)}{' '}
            {isDeposit ? `virés sur ${goal.name}.` : `virés depuis ${goal.name}.`}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 w-full rounded-xl bg-series py-3 text-sm font-medium text-white"
          >
            Terminé
          </button>
        </>
      ) : (
        <>
          <div className="mt-4">
            <AmountField
              label="Montant à virer"
              valueCents={amountCents}
              onChange={setAmountCents}
              hint={
                isDeposit
                  ? 'L’argent part vraiment du compte courant vers le compte d’épargne.'
                  : `Disponible : ${formatEUR(goal.savedCents)}`
              }
            />
          </div>

          {overCeiling && (
            <p className="mt-2 text-xs text-critical">
              Le pôle ne contient que {formatEUR(goal.savedCents)}.
            </p>
          )}

          {overSuggestion && !overCeiling && (
            <p className="mt-2 text-xs text-critical">
              <span aria-hidden="true">▼</span> Au-delà de votre avance : ce virement vous fera
              passer en retard sur le mois.
            </p>
          )}

          {error !== null && (
            <p role="alert" className="mt-2 text-sm text-critical">
              {error}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border border-line py-3 text-sm"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={pending || amountCents <= 0 || overCeiling}
              onClick={() => void submit()}
              className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
            >
              Virer
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}
